import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

const zpaCommonFields = {
  zidentityVanityDomain: Element({
    label: "ZIdentity vanity domain",
    type: "string",
    description:
      "The full ZIdentity vanity domain, e.g. https://yourcompany.zslogin.net",
    validator: async (_id, field) => {
      if (!field) {
        return "Please provide the ZIdentity vanity domain";
      }
      const urlPattern = /^https:\/\/[a-zA-Z0-9-]+\.zslogin\.net$/;
      if (!field.match(urlPattern)) {
        return "ZIdentity vanity domain must be in the format: https://yourcompany.zslogin.net";
      }
    },
  }),
  customerId: Element({
    label: "Customer ID",
    type: "string",
    description: 'The customer ID from the ZPA admin console "Account" page',
    validator: async (_id, field) => {
      if (!field) {
        return "Please provide the customer ID";
      }
      // This is somewhat arbitrary, permissive pattern. It's not documented what exactly the format is.
      const customerIdPattern = /^[a-zA-Z0-9_-]{3,20}$/;
      if (!field.match(customerIdPattern)) {
        return "Customer ID must be an alphanumeric string with underscores and hyphens, between 3 and 20 characters";
      }
    },
  }),
  clientId: Element({
    label: "Client ID",
    type: "string",
    description: "OneAPI Client ID for Zscaler Private Access",
    validator: async (_id, field) => {
      if (!field) {
        return "Please provide the Client ID";
      }
      const clientIdPattern = /^[a-zA-Z0-9]{3,20}$/;
      if (!field.match(clientIdPattern)) {
        return "Client ID must be an alphanumeric string between 3 and 20 characters";
      }
    },
  }),
} as const;

const clientSecretValidator = async (
  _id: string,
  field: { clearText?: string } | undefined
) => {
  if (!field) {
    return "Please provide the Client Secret";
  }
  if (field.clearText?.trim() !== field.clearText) {
    return "Please remove leading and trailing whitespace from the Client Secret";
  }
  const clientSecretPattern = /^\w{3,60}$/;
  if (!field.clearText?.match(clientSecretPattern)) {
    return "Client Secret must be an alphanumeric string between 3 and 60 characters";
  }
};

const iamWriteZpaSchema = {
  clientId: zpaCommonFields.clientId,
  accessManagementClientSecret: Element({
    label: "Client secret",
    type: "encrypted",
    description: "OneAPI Client Secret for Zscaler Private Access",
    validator: clientSecretValidator,
  }),
} as const;

const networkAccessZpaSchema = {
  ...zpaCommonFields,
  networkAccessClientSecret: Element({
    label: "Client secret",
    type: "encrypted",
    description: "OneAPI Client Secret for Zscaler Private Access",
    validator: clientSecretValidator,
  }),
} as const;

export const ZpaComponents = InstallSpec({
  "iam-write": ItemComponent({
    label: "Zscaler Private Access policy management",
    description:
      "Allows P0 to manage access to your Zscaler Private Access network for your users. Requires ZIdentity and OneAPI.",
    type: "dynamic",
    maturity: "preview",
    schema: iamWriteZpaSchema,
  }),
  "network-access": ItemComponent({
    label: "Zscaler Private Access network access",
    description:
      "Allows P0 to resolve Zscaler Private Access application segments for self-hosted SSH instances. Requires ZIdentity and OneAPI.",
    type: "string",
    maturity: "preview",
    schema: networkAccessZpaSchema,
  }),
});
